<template>
	<!-- Modern Task Card Design -->
	<div
		v-if="displayRow"
		class="task-card"
		:class="[
			`priority-${localTask.priority || 'neutral'}`,
			{ 'task-completed': localTask.completed === 'Y' },
			{ 'task-overdue': isOverdue }
		]"
	>
		<!-- Card Header (Collapsed State) -->
		<div class="task-card-header" @click.self="toggleDetail" style="cursor: pointer;">
			<!-- Checkbox -->
			<div class="task-checkbox" @click.stop>
				<b-icon-circle
					v-if="localTask.completed !== 'Y'"
					:id="'task-complete-' + localTask.id"
					variant="secondary"
					style="width:24px; height:24px; cursor:pointer;"
					@click="toggleTaskFlag('completed')"
				/>
				<b-icon-check-circle-fill
					v-else
					:id="'task-complete-' + localTask.id"
					variant="success"
					style="width:24px; height:24px; cursor:pointer;"
					@click="toggleTaskFlag('completed')"
				/>
				<b-tooltip :target="'task-complete-' + localTask.id" triggers="hover">
					{{ localTask.completed === 'Y' ? 'Mark Incomplete' : 'Mark Complete' }}
				</b-tooltip>
			</div>

			<!-- Main Content -->
			<div class="task-card-main" @click="toggleDetail">
				<!-- Title -->
				<h3 class="task-card-title">
					<!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- Star icon inside title input like modal -->
					<!-- WHY: User requested star to be inside input field, matching TaskEntryForm modal pattern -->
					<!-- 20260129 -- Copilot (Claude Sonnet 4.5) -- Conditional router-link only when task has real _id -->
					<!-- WHY: Template instantiation shows tasks without IDs yet, causing router param errors -->
					<b-link
						v-if="!openInEditMode && editingTaskId !== localTask.id && localTask._id && !localTask._id.toString().startsWith('TEMP')"
						:to="{ name: 'task-detail', params: { taskId: localTask._id } }"
					>
						<b-icon-star-fill
							v-if="localTask.star === 'Y'"
							variant="warning"
							style="width:16px; height:16px; cursor:pointer; margin-right: 4px;"
							@click.stop="toggleTaskFlag('star')"
						/>
						<b-icon-star
							v-else
							variant="secondary"
							style="width:16px; height:16px; cursor:pointer; margin-right: 4px;"
							@click.stop="toggleTaskFlag('star')"
						/>
						{{ task.title }}
					</b-link>
					<span
						v-else-if="!openInEditMode && editingTaskId !== localTask.id"
					>
						<b-icon-star-fill
							v-if="localTask.star === 'Y'"
							variant="warning"
							style="width:16px; height:16px; cursor:pointer; margin-right: 4px;"
							@click.stop="toggleTaskFlag('star')"
						/>
						<b-icon-star
							v-else
							variant="secondary"
							style="width:16px; height:16px; cursor:pointer; margin-right: 4px;"
							@click.stop="toggleTaskFlag('star')"
						/>
						{{ task.title }}
					</span>
					<b-input-group v-else size="sm" style="font-size: 0.875rem; font-weight: normal;" @click.stop>
						<template v-slot:prepend>
							<b-input-group-text>
								<b-icon-star-fill
									v-if="localTask.star === 'Y'"
									variant="warning"
									style="width:16px; height:16px; cursor:pointer;"
									@click.stop="toggleTaskFlag('star')"
								/>
								<b-icon-star
									v-else
									variant="secondary"
									style="width:16px; height:16px; cursor:pointer;"
									@click.stop="toggleTaskFlag('star')"
								/>
							</b-input-group-text>
						</template>
						<b-form-input
							v-model="localTask.title"
							:state="taskValidation.title"
							size="sm"
						/>
					</b-input-group>
				</h3>

				<!-- Meta Info (Collapsed) -->
				<div v-if="!detailExpanded && !openInEditMode && editingTaskId !== localTask.id" class="task-card-meta">
				<!-- Start Date/Time -->
				<span v-if="localTask.start" class="task-badge badge-neutral">
					<b-icon icon="play-circle" style="width: 12px; height: 12px;" />
					{{ startDate }} {{ startTime }}
				</span>

				<!-- Due Date/Time -->
				<span v-if="localTask.due" class="task-badge" :class="{ 'badge-overdue': isOverdue, 'badge-due-today': isDueToday, 'badge-neutral': !isOverdue && !isDueToday }">
					<b-icon icon="calendar-check" style="width: 12px; height: 12px;" />
					{{ dueDateDisplay }} {{ dueTime }}
				</span>

				<!-- Priority Badge -->
				<span v-if="localTask.priority && localTask.priority !== 'neutral'" class="task-badge" :class="`badge-priority-${localTask.priority}`">
					<b-icon 
						:icon="priorityIcon" 
						style="width: 12px; height: 12px;"
					/>
					{{ priorityLabel }}
				</span>

				<!-- Status -->
				<span v-if="localTask.status" class="task-badge badge-neutral">
					<b-icon icon="check-circle" style="width: 12px; height: 12px;" />
					{{ localTask.status }}
				</span>

				<!-- Context -->
				<!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Apply tinted badge style when a context color is assigned -->
				<span v-if="localTask.context" class="task-badge badge-neutral" :style="contextBadgeStyle">
					<b-icon icon="tag" style="width: 12px; height: 12px;" />
					{{ localTask.context }}
				</span>

				<!-- Folder -->
				<!-- 20260416 -- Copilot (Claude Sonnet 4.6) -- Apply tinted badge style when a folder color is assigned -->
				<span v-if="localTask.folder" class="task-badge badge-neutral" :style="folderBadgeStyle">
					<b-icon icon="folder" style="width: 12px; height: 12px;" />
					{{ localTask.folder }}
				</span>

				<!-- Children Count -->
				<span v-if="localTask.children && localTask.children.length > 0" class="task-badge badge-neutral">
					<b-icon icon="diagram-3" style="width: 12px; height: 12px;" />
					{{ localTask.children.length }}
				</span>

				<!-- Parent Link (Collapsed) -->
				<!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- Show parent link in collapsed view -->
				<!-- WHY: Child tasks must always provide link to parent regardless of collapsed/expanded state -->
				<!-- 20260129 -- Copilot (Claude Sonnet 4.5) -- Only show router-link if parent has real ID -->
				<!-- WHY: Template tasks don't have real IDs yet, causes router param errors -->
				<span v-if="parentTaskName != 'N/A' && parentTaskId() && !parentTaskId().toString().startsWith('TEMP')" class="task-badge badge-neutral" style="cursor: pointer;">
					<b-icon icon="arrow-return-right" style="width: 12px; height: 12px;" />
					<router-link :to="{ name: 'task-detail', params: { taskId: parentTaskId() } }" style="color: inherit; text-decoration: none;">
						{{ parentTaskName }}
					</router-link>
				</span>
				<span v-else-if="parentTaskName != 'N/A'" class="task-badge badge-neutral">
					<b-icon icon="arrow-return-right" style="width: 12px; height: 12px;" />
					{{ parentTaskName }}
				</span>
			</div>

				<!-- Notes Preview (Collapsed) -->
			<vue-markdown v-if="!hideDetail && !openInEditMode && editingTaskId !== localTask.id && localTask.notes" class="task-card-preview" :source="localTask.notes" />
			</div>

			<!-- Actions -->
			<div class="task-card-actions" @click.stop>

				<b-dropdown right variant="link" no-caret size="sm">
					<template #button-content>
						<b-icon icon="three-dots-vertical" variant="secondary" />
					</template>
					<b-dropdown-item v-if="!openInEditMode && editingTaskId !== localTask.id" @click="enterTaskEditMode(localTask.id)">
						<b-icon icon="pencil" /> Edit
					</b-dropdown-item>
					<b-dropdown-item @click="emitCreateChildTask(localTask._id)">
						<b-icon icon="file-plus" /> Add Child
					</b-dropdown-item>
					<!-- 20260507 -- Copilot (Claude Sonnet 4.6) -- Share task: $root event so modal is reachable from any list page -->
					<b-dropdown-item v-if="localTask._id" @click="$root.$emit('open:shareTask', localTask)">
						<b-icon icon="share" /> Share
					</b-dropdown-item>
					<b-dropdown-divider />
					<b-dropdown-item variant="danger" @click="emitDelete(localTask._id)">
						<b-icon icon="trash" /> Delete
					</b-dropdown-item>
				</b-dropdown>

				<button
					class="task-expand-toggle"
					:class="{ expanded: hideDetail }"
				@click.stop="toggleDetail"
			>
				<b-icon :icon="hideDetail ? 'chevron-up' : 'chevron-down'" />
			</button>
		</div>
	</div>

		<!-- Expanded Detail Section -->
		<b-collapse :visible="hideDetail" class="task-card-detail">
			<!-- Timeline & Length Section -->
			<div class="detail-section">
				<h4 class="detail-section-title">Timeline & Duration</h4>
				<b-row>
					<b-col md="3">
						<label class="detail-label">Start Date</label>
						<b-form-datepicker
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="startDateForSystem"
							:date-format-options="dateFormatOptions"
							:today-button="true"
							:reset-button="true"
							size="sm"
						/>
						<div v-else class="detail-value">{{ startDate }}</div>
					</b-col>
					<b-col md="2">
						<label class="detail-label">Start Time</label>
						<b-form-input v-if="openInEditMode || (editingTaskId == localTask.id)" v-model.lazy="startTimeForSystem" type="time" size="sm" />
						<div v-else class="detail-value">{{ startTime }}</div>
					</b-col>
					<b-col md="3">
						<label class="detail-label">Due Date</label>
						<b-form-datepicker
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="dueDateForSystem"
							:date-format-options="dateFormatOptions"
							:today-button="true"
							:reset-button="true"
							size="sm"
						/>
						<div v-else class="detail-value">{{ dueDate }}</div>
					</b-col>
					<b-col md="2">
						<label class="detail-label">Due Time</label>
						<b-form-input v-if="openInEditMode || (editingTaskId == localTask.id)" v-model="dueTimeForSystem" type="time" size="sm" />
						<div v-else class="detail-value">{{ dueTime }}</div>
					</b-col>
					<b-col md="2">
						<label class="detail-label">Length (min)</label>
						<b-form-input
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model.number="localTask.length"
							data-testid="inline-task-length"
							type="number"
							size="sm"
						/>
						<div v-else class="detail-value">{{ localTask.length }}</div>
					</b-col>
				</b-row>
			</div>

			<!-- Repeat & Reminders Section -->
			<div class="detail-section">
				<h4 class="detail-section-title">Repeat & Reminders</h4>
				<b-row>
					<b-col md="6">
						<label class="detail-label">Repeat</label>
						<b-form-select
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="localTask.repeat"
							data-testid="inline-task-repeat"
							:options="validRepeatFrequencies"
							value-field="item"
							text-field="name"
							size="sm"
						/>
						<div v-else class="detail-value">{{ repeat }}</div>
						
						<b-input-group v-if="(openInEditMode || editingTaskId == localTask.id) && localTask.repeat == 'ADVANCED'" class="mt-1" size="sm">
							<b-form-input
								v-model="repeatFinal"
								placeholder="e.g. every 4 days, every 2nd monday"
							/>
							<template v-slot:append>
								<b-button variant="outline-secondary" size="sm">
									<b-icon icon="question-circle" />
								</b-button>
							</template>
						</b-input-group>

						<!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- Radio group for repeat from option in inline edit -->
						<!-- WHY: Control when repeat calculates from (completed date or due date) -->
						<!-- UPDATED: 20260127 -- Using explicit :model-value/@update:modelValue for Vue 3 compatibility -->
						<b-form-group v-if="(openInEditMode || editingTaskId == localTask.id) && localTask.repeat != 'NONE'" class="mt-1 mb-0">
							<b-form-radio-group
								:model-value="localTask.repeatFrom"
								@update:modelValue="value => $set(localTask, 'repeatFrom', value)"
								name="repeatFromInline"
								size="sm"
							>
								<b-form-radio value="completedDate">From Completed</b-form-radio>
								<b-form-radio value="due">From Due</b-form-radio>
							</b-form-radio-group>
						</b-form-group>
					</b-col>
					<b-col md="6">
						<label class="detail-label">Reminder</label>
						<b-form-select
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="localTask.remind"
							data-testid="inline-task-remind"
							:options="validReminderFrequencies"
							value-field="item"
							text-field="name"
							size="sm"
						/>
						<div v-else class="detail-value">{{ remind }}</div>
					</b-col>
				</b-row>
			</div>

			<!-- Properties Section -->
			<div class="detail-section">
				<h4 class="detail-section-title">Properties</h4>
				<b-row>
					<b-col md="3">
						<label class="detail-label">Priority</label>
						<b-form-select
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="localTask.priority"
							data-testid="inline-task-priority"
							:options="validPriorities"
							value-field="item"
							text-field="name"
							size="sm"
						/>
						<div v-else class="detail-value">
							<span v-if="localTask.priority" class="task-badge" :class="`badge-priority-${localTask.priority}`">
								{{ localTask.priority }}
							</span>
						</div>
					</b-col>
					<b-col md="3">
						<label class="detail-label">Status</label>
						<b-form-select
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="localTask.status"
							data-testid="inline-task-status"
							:options="validStatuses"
							value-field="item"
							text-field="name"
							size="sm"
						/>
						<div v-else class="detail-value">{{ localTask.status }}</div>
					</b-col>
					<b-col md="3">
						<label class="detail-label">Folder</label>
						<b-form-select
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="localTask.folder"
							data-testid="inline-task-folder"
							:options="$store.getters.getFolders"
							size="sm"
						/>
						<div v-else class="detail-value">{{ localTask.folder }}</div>
					</b-col>
					<b-col md="3">
						<label class="detail-label">Context</label>
						<b-form-select
							v-if="openInEditMode || (editingTaskId == localTask.id)"
							v-model="localTask.context"
							data-testid="inline-task-context"
							:options="$store.getters.getContexts"
							value-field="name"
							text-field="name"
							size="sm"
						/>
						<div v-else class="detail-value">{{ localTask.context }}</div>
					</b-col>
				</b-row>
			</div>

			<!-- Notes Section -->
			<div v-if="(openInEditMode || editingTaskId == localTask.id) || (localTask.notes && localTask.notes.length > 0)" class="detail-section">
				<h4 class="detail-section-title">Notes</h4>
				<b-form-textarea
					v-if="openInEditMode || (editingTaskId == localTask.id)"
					v-model="localTask.notes"
					data-testid="inline-task-notes"
					placeholder="Supports Markdown."
					rows="6"
					size="sm"
				/>
				<vue-markdown v-else class="detail-value markdown-content" :source="localTask.notes" />
			</div>

			<!-- Children Section -->
			<div v-if="!childrenDisabled && childCount > 0" class="detail-section">
				<h4 class="detail-section-title">
					Subtasks ({{ childCount }})
					<b-link @click="toggleChildDetail()">
						<b-icon :icon="hideChildrenIcon" />
					</b-link>
				</h4>
				<b-collapse :visible="hideChildren">
					<b-table striped hover :items="task.children" :fields="childFields" :responsive="true" size="sm">
						<template v-slot:cell(_id)="data">
							<b-icon-circle
								v-if="data.item.completed == 'N'"
								variant="secondary"
								style="width:18px; height:18px;"
							/>
							<b-icon-check-circle-fill
								v-else
								variant="success"
								style="width:18px; height:18px;"
							/>
						</template>
						<template v-slot:cell(title_id)="data">
						<!-- 20260129 -- Copilot (Claude Sonnet 4.5) -- Only link if child has real ID -->
						<b-link v-if="data.item._id && !data.item._id.toString().startsWith('TEMP')" :to="{ name: 'task-detail', params: { taskId: data.item._id } }">
							{{ data.item.title }}
						</b-link>
						<span v-else>
							{{ data.item.title }}
						</span>
					</template>
					<template v-slot:cell(startstarttime)="data">
							{{ convertMillisForDisplay(data.item.start, 'MM/DD/YYYY') }}
							{{ convertMillisForDisplay(data.item.starttime, 'HH:mm a') }}
						</template>
						<template v-slot:cell(dueduetime)="data">
							{{ convertMillisForDisplay(data.item.due, 'MM/DD/YYYY') }}
							{{ convertMillisForDisplay(data.item.duetime, 'HH:mm a') }}
						</template>
					</b-table>
				</b-collapse>
			</div>

			<!-- Metadata Footer -->
			<div v-if="addedDate || parentTaskName != 'N/A'" class="detail-metadata">
				<small class="text-muted">
					<span v-if="addedDate">Created {{ addedDate }}</span>
					<!-- 20260129 -- Copilot (Claude Sonnet 4.5) -- Only link if parent has real ID -->
					<span v-if="parentTaskName != 'N/A' && parentTaskId() && !parentTaskId().toString().startsWith('TEMP')" class="float-right">
						Parent:
						<b-link :to="{ name: 'task-detail', params: { taskId: parentTaskId() } }">
							{{ parentTaskName }}
						</b-link>
					</span>
					<span v-else-if="parentTaskName != 'N/A'" class="float-right">
						Parent: {{ parentTaskName }}
					</span>
				</small>
			</div>
		</b-collapse>

		<!-- Action Footer (Edit Mode) -->
		<!-- 20260127 -- Copilot (Claude Sonnet 4.5) -- Inline editor action buttons -->
		<!-- WHY: Delete left-aligned, Cancel/Save right-aligned with proper color scheme and sizing -->
		<div v-if="openInEditMode || (editingTaskId == localTask.id)" class="task-card-actions-footer">
			<b-button
				v-if="!toolsDisabled"
				size="sm"
				variant="danger"
				@click="emitDelete(localTask._id)"
			>
				Delete
			</b-button>
			<b-button
				size="sm"
				variant="warning"
				class="ml-auto"
				@click="cancelTaskEditMode()"
			>
				Cancel
			</b-button>
			<b-button
				size="sm"
				variant="success"
				@click="saveTask()"
			>
				Save
			</b-button>
		</div>
	</div>
</template>

<script>
import TaskService from '../services/TaskService'
import VueMarkdown from 'vue-markdown'

import taskOps from '../mixins/taskOps'
import genericOps from '../mixins/genericOps'

export default {
	name: 'TaskTableRow',
	components: {
		'vue-markdown': VueMarkdown,
	},
	mixins: [taskOps, genericOps],
	emits: ['createChild:task', 'delete:task'],
	props: {
		childrenDisabled: {
			type: Boolean,
			default: false
		},
		childrenExpanded: {
			type: Boolean,
			default: false,
		},
		detailExpanded: {
			type: Boolean,
			default: false,
		},
		openInEditMode: {
			type: Boolean,
			default: false
		},
		task: {
			type: Object,
			default: {
				id: null,
				children: [],
			},
		},
		toolsDisabled: {
			type: Boolean,
			default: false
		}
	},
	data() {
		return {
			childFields: [
				{ key: '_id', label: '' },
				{ key: 'title_id', label: 'Title' },
				{ key: 'startstarttime', label: 'Start Date' },
				{ key: 'dueduetime', label: 'Due Date' },
				{ key: 'priority', label: 'Priority' },
				{ key: 'status', label: 'Status' },
			],
			dateFormatOptions: { year: 'numeric', month: 'numeric', day: '2-digit', weekday: 'short' },
			hideChildren: this.childrenExpanded,
			hideChildrenIcon: 'chevron-compact-left',
		hideDetail: this.detailExpanded,
			hideDetailIcon: 'chevron-compact-right',
			localTask: {
				_id: null,
				children: [],
			},
		}
	},
	computed: {
		childCount: function() {
			if (typeof this.task.children != 'undefined' && this.task.children !== null) {
				return this.task.children.length
			} else {
				return 0
			}
		},
		isOverdue: function() {
			if (!this.localTask.due) return false;
			if (this.localTask.completed === 'Y') return false;
			
			const now = new Date();
			
			// If duetime exists, compare full datetime
			if (this.localTask.duetime && this.localTask.duetime > 0) {
				const dueDateTime = new Date(this.localTask.duetime);
				return dueDateTime < now;
			}
			
			// Otherwise, compare just the date (end of due date)
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const dueDate = new Date(this.localTask.due);
			dueDate.setHours(0, 0, 0, 0);
			return dueDate < today;
		},
		isDueToday: function() {
			if (!this.localTask.due) return false;
			
			const now = new Date();
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const dueDate = new Date(this.localTask.due);
			dueDate.setHours(0, 0, 0, 0);
			
			// Check if due date is today
			if (dueDate.getTime() !== today.getTime()) return false;
			
			// If there's a duetime, check if it hasn't passed yet
			if (this.localTask.duetime && this.localTask.duetime > 0) {
				const dueDateTime = new Date(this.localTask.duetime);
				return dueDateTime >= now; // Only "due today" if time hasn't passed
			}
			
			// No time specified, so it's due today (anytime today)
			return true;
		},
		dueDateDisplay: function() {
			if (!this.localTask.due) return null;
			
			const today = new Date();
			today.setHours(0, 0, 0, 0);
			const dueDate = new Date(this.localTask.due);
			dueDate.setHours(0, 0, 0, 0);
			
			const tomorrow = new Date(today);
			tomorrow.setDate(tomorrow.getDate() + 1);
			
			if (dueDate < today) {
				return 'Overdue ' + this.convertMillisForDisplay(this.localTask.due, 'MM/DD/YYYY');
			} else if (dueDate.getTime() === today.getTime()) {
				return 'Due Today';
			} else if (dueDate.getTime() === tomorrow.getTime()) {
				return 'Due Tomorrow';
			} else {
				// Format as "Due Mon, Jan 15"
				const options = { weekday: 'short', month: 'short', day: 'numeric' };
				return 'Due ' + dueDate.toLocaleDateString('en-US', options);
			}
		},
		notesPreview: function() {
			if (!this.localTask.notes) return '';
			
			// Convert markdown to plain text
			let text = this.localTask.notes
				// Remove HTML tags
				.replace(/<[^>]*>/g, '')
				// Remove markdown headers
				.replace(/^#{1,6}\s+/gm, '')
				// Remove markdown bold/italic
				.replace(/(\*\*|__)(.*?)\1/g, '$2')
				.replace(/(\*|_)(.*?)\1/g, '$2')
				// Remove markdown links but keep text
				.replace(/\[([^\]]+)\]\([^)]+\)/g, '$1')
				// Remove markdown images
				.replace(/!\[([^\]]*)\]\([^)]+\)/g, '')
				// Remove inline code backticks
				.replace(/`([^`]+)`/g, '$1')
				// Remove blockquote markers
				.replace(/^>\s+/gm, '')
				// Remove list markers
				.replace(/^[\*\-\+]\s+/gm, '')
				.replace(/^\d+\.\s+/gm, '')
				// Collapse multiple spaces/newlines into single space
				.replace(/\s+/g, ' ')
				.trim();
			
			return text;
		},
		priorityIcon: function() {
			const icons = {
				top: 'chevron-double-up',
				high: 'arrow-up',
				medium: 'dash',
				low: 'arrow-down',
				negative: 'chevron-double-down'
			};
			return icons[this.localTask.priority] || 'dash';
		},
		priorityLabel: function() {
			const labels = {
				top: 'Top',
				high: 'High',
				medium: 'Medium',
				low: 'Low',
				negative: 'Negative'
			};
			return labels[this.localTask.priority] || this.localTask.priority;
		},
		// Local date/time computed properties for display
		startDate: function() {
			return TaskService.getStringFromMillis(this.localTask.start, 'MM/DD/YYYY');
		},
		startTime: function() {
			return TaskService.getStringFromMillis(this.localTask.starttime, 'hh:mm a');
		},
		dueDate: function() {
			return TaskService.getStringFromMillis(this.localTask.due, 'MM/DD/YYYY');
		},
		dueTime: function() {
			return TaskService.getStringFromMillis(this.localTask.duetime, 'hh:mm a');
		},
		displayRow: function() {
			
			// 20260128 -- Copilot (Claude Sonnet 4.5) -- Added null check for task
			// WHY: When deleting a task, component may re-render with null/undefined task
			if (!this.task) {
				return false
			}
			
			// Neither - No parent, no children
			// Parent - No parent, have children
			// Child - Has parent, may have children
			
			
			if (this.parentTaskId() == 'N/A') {
				
				var numChildren = (this.task.children || []).length;
				if(numChildren == 0) {
					// Always display: Task has no parent and has no children	
					return true;
				} else {
					// Optionally display: Task has no parent as has children
					try {
						if (
							typeof this.$store.getters.getUserPrefs.taskSortOrder.hideParents != 'undefined' &&
							this.$store.getters.getUserPrefs.taskSortOrder.hideParents !== null &&
							this.$store.getters.getUserPrefs.taskSortOrder.hideParents
						) {
							return false
						} else {
							return true
						}
					} catch (e) {
						return true
					}
				}
			} else {
				// Task is a child.
				try {
					if (
						typeof this.$store.getters.getUserPrefs.taskSortOrder.hideChildren != 'undefined' &&
						this.$store.getters.getUserPrefs.taskSortOrder.hideChildren !== null &&
						this.$store.getters.getUserPrefs.taskSortOrder.hideChildren
					) {
						return false
					} else {
						return true
					}
				} catch (e) {
					return true
				}
			}
		},
		// localTask: {
		// 	get: function() {
		// 		return this.task
		// 	},
		// 	set: function(newVal) {
		// 		////console.log('TaskTableRow.localTask.set()')
		// 		this.task = newVal
		// 	},
		// },
		parentTaskName: function() {
			if (
				typeof this.task.parentTask != 'undefined' &&
				this.task.parentTask !== null &&
				this.task.parentTask.length > 0
			) {
				return this.task.parentTask[0].title
			} else if (
				// 20260127 -- Copilot (Claude Sonnet 4.5) -- Fallback to check parent field directly
				// WHY: If parentTask array isn't populated, still indicate this is a child task
				typeof this.task.parent != 'undefined' &&
				this.task.parent !== null &&
				this.task.parent !== ''
			) {
				return '(Parent)'
			} else {
				return 'N/A'
			}
		},
		// 20260416 -- Copilot (Claude Sonnet 4.6) -- Dynamic badge style based on assigned context color
		contextBadgeStyle() {
			const color = this.$store.getters.getContextColorMap[this.localTask.context]
			if (!color) return {}
			return { backgroundColor: color + '1a', color }
		},
		// 20260416 -- Copilot (Claude Sonnet 4.6) -- Dynamic badge style based on assigned folder color
		folderBadgeStyle() {
			const color = this.$store.getters.getFolderColorMap[this.localTask.folder]
			if (!color) return {}
			return { backgroundColor: color + '1a', color }
		},
	},
	watch: {
		hideChildren: function(newVal) {
			this.hideChildren = newVal

			if (this.hideChildren) {
				this.hideChildrenIcon = 'chevron-compact-down'
			} else {
				this.hideChildrenIcon = 'chevron-compact-left'
			}
		},
		hideDetail: function(newVal) {
			this.hideDetail = newVal

			if (this.hideDetail) {
				this.hideDetailIcon = 'chevron-compact-down'
			} else {
				this.hideDetailIcon = 'chevron-compact-right'
			}
		},
		childrenExpanded: function(newVal) {
			this.hideChildren = newVal

			if (this.hideChildren) {
				this.hideChildrenIcon = 'chevron-compact-down'
			} else {
				this.hideChildrenIcon = 'chevron-compact-left'
			}
		},
		detailExpanded: function(newVal) {
			this.hideDetail = newVal

			if (this.hideDetail) {
				this.hideDetailIcon = 'chevron-compact-down'
			} else {
				this.hideDetailIcon = 'chevron-compact-right'
			}
		},
	},
	beforeMount() {
			this.localTask = this.task;
		},
	methods: {
		convertMillisForDisplay(value, format) {
			// 20200321 -- ZWS -- Created because, apparently, the component
			// doesn't have access to TaskService from its template section

			return TaskService.getStringFromMillis(value, format)
		},
		parentTaskId() {
			// 20260128 -- Copilot (Claude Sonnet 4.5) -- Added null check for task
			// WHY: When deleting a task, component may re-render with null task
			if (!this.task) {
				return 'N/A'
			}
			
			if (
				typeof this.task.parentTask != 'undefined' &&
				this.task.parentTask !== null &&
				this.task.parentTask.length > 0
			) {
				return this.task.parentTask[0]._id || ""
			} else if (
				// 20260127 -- Copilot (Claude Sonnet 4.5) -- Fallback to parent field directly
				// WHY: If parentTask array isn't populated, use the parent ID field
				typeof this.task.parent != 'undefined' &&
				this.task.parent !== null &&
				this.task.parent !== ''
			) {
				return this.task.parent
			} else {
				return 'N/A'
			}
		},
		emitCreateChildTask() {
			console.log('TaskTableRow.emitCreateChildTask - this.task =', this.task)
			console.log('TaskTableRow.emitCreateChildTask - this.task._id =', this.task._id)
			this.$emit('createChild:task', this.task)
		},
		emitDelete(id) {
			// 20260128 -- Copilot (Claude Sonnet 4.5) -- Prevent deletion of parent tasks with children
			// WHY: Deleting a parent task would orphan child tasks
			
			// 20260129 -- Copilot (Claude Sonnet 4.5) -- Added null check for task ID
			// WHY: localTask.id can be null if component hasn't fully initialized
			// Use _id field since MongoDB tasks don't have an id field
			if (!id && this.task) {
				id = this.task._id
			}
			
			if (!id) {
				console.error('TaskTableRow.emitDelete: Cannot delete task - ID is null')
				return
			}
			
			const numChildren = (this.task?.children || []).length
			if (numChildren > 0) {
				this.$bvToast.toast(
					`This task has ${numChildren} child task${numChildren > 1 ? 's' : ''}. Please delete or reassign the child tasks first.`,
					{
						title: 'Cannot Delete Parent Task',
						variant: 'warning',
						solid: true,
						toaster: 'b-toaster-top-center',
						autoHideDelay: 5000
					}
				)
				return
			}
			this.$emit('delete:task', id)
		},
		/*
        20200318 -- I hope this is gone.
        toggle(element, id) {
            
            ////////console.log('Toggling: ' + element + '-' + id)
            
            let ele = this.$refs[element + "-" + id]

            if(ele.classList.contains('invisible')) {
                ele.classList.replace("invisible", 'visible')	
                ele.classList.replace("d-none", 'd-inline')
            } else if(ele.classList.contains('visible')) {
                ele.classList.replace("visible", 'invisible')	
                ele.classList.replace("d-inline", 'd-none')	
            }


        },
        */
		toggleChildDetail() {
			// 20200318 -- Expand/collapse child tasks (aka sub tasks)
			this.hideChildren = !this.hideChildren
		},
		toggleDetail() {
			// 20260127 -- Copilot (Claude Sonnet 4.5) -- Cancel inline edit when collapsing
			// WHY: Collapsing should cancel any in-progress edits and then collapse
			if(this.openInEditMode || this.editingTaskId === this.localTask.id) {
				this.cancelTaskEditMode()
			}
			
			// 20200318 -- Expand/collapse task detail
			this.hideDetail = !this.hideDetail
		},
		// translateDateTime(date, time) {
		//     let value = TaskService.translateUnixDateForDisplay(date, time)
		//     return value
		// },
		// 20200318 -- Hopefully this isn't needed
		// translateRepeat(repeat) {

		//     let value ='';

		//     try {
		//         value = TaskService.translateiCalRRuleForDisplay(repeat)
		//     } catch(e) {
		//         //////console.log(e)
		//         value = repeat
		//     }

		//     return value;
		// },
	},
}
</script>

<style scoped>
.text-secondary font-weight-bold {
	color: grey;
	font-weight: 500;
}
</style>
